/**
 * EditSectorDialog.jsx - Dialog pour modifier un secteur existant
 *
 * Permet de modifier:
 * - Nom du secteur
 * - Compartiment d'appartenance
 * - Point GPS central (optionnel)
 * - Métadonnées
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close, MyLocation, Edit as EditIcon } from '@mui/icons-material';
import FarmMapService from './FarmMapService';

/**
 * Dialog pour éditer un secteur
 */
const EditSectorDialog = ({
  open,
  onClose,
  farmId,
  sector,
  compartments = [],
  onSectorUpdated
}) => {
  // État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    compartment_id: '',
    gps_latitude: '',
    gps_longitude: ''
  });

  // État de l'interface
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Pré-remplir le formulaire avec les données du secteur
  useEffect(() => {
    if (open && sector) {
      setFormData({
        name: sector.name || '',
        compartment_id: sector.compartment_id || '',
        gps_latitude: sector.gps_latitude || '',
        gps_longitude: sector.gps_longitude || ''
      });
      setError('');
    }
  }, [open, sector]);

  /**
   * Gérer les changements de champ
   */
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  /**
   * Obtenir la position GPS de l'utilisateur
   */
  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur');
      return;
    }

    setIsGettingLocation(true);
    setError('');

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      setFormData(prev => ({
        ...prev,
        gps_latitude: position.coords.latitude.toFixed(6),
        gps_longitude: position.coords.longitude.toFixed(6)
      }));
    } catch (err) {
      setError(`Impossible d'obtenir la position: ${err.message}`);
    } finally {
      setIsGettingLocation(false);
    }
  };

  /**
   * Valider le formulaire
   */
  const validateForm = () => {
    // Validation coordonnées si fournies
    if (formData.gps_latitude || formData.gps_longitude) {
      const lat = parseFloat(formData.gps_latitude);
      const lng = parseFloat(formData.gps_longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError('Latitude invalide (-90 à 90)');
        return false;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError('Longitude invalide (-180 à 180)');
        return false;
      }
    }

    return true;
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Mise à jour du secteur
      const updateData = {
        name: formData.name.trim() || undefined,
        compartmentId: formData.compartment_id || undefined,
        gpsLatitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : undefined,
        gpsLongitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : undefined,
        metadata: {
          updated_at: new Date().toISOString(),
          updated_via: 'map_interface'
        }
      };

      const updatedSector = await FarmMapService.updateSector(sector.sector_id, updateData);

      // Notifier le parent
      if (onSectorUpdated) {
        onSectorUpdated(updatedSector);
      }

      // Fermer le dialog
      onClose();

    } catch (err) {
      console.error('Erreur modification secteur:', err);
      setError(err.message || 'Erreur lors de la modification du secteur');
    } finally {
      setLoading(false);
    }
  };

  if (!sector) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Modifier le secteur</Typography>
          <IconButton onClick={onClose} disabled={loading} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* ID du secteur (lecture seule) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ID du secteur"
              value={sector.sector_id}
              disabled
              helperText="L'identifiant ne peut pas être modifié"
            />
          </Grid>

          {/* Nom du secteur */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nom du secteur"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="Ex: Serre 1 - Ouest"
              disabled={loading}
              helperText="Nom descriptif du secteur"
            />
          </Grid>

          {/* Compartiment */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="compartment-select-label">Compartiment (optionnel)</InputLabel>
              <Select
                labelId="compartment-select-label"
                value={String(formData.compartment_id)}
                label="Compartiment (optionnel)"
                onChange={handleChange('compartment_id')}
                disabled={loading || compartments.length === 0}
              >
                <MenuItem value="">
                  <em>Aucun compartiment</em>
                </MenuItem>
                {compartments.map((compartment) => (
                  <MenuItem key={compartment.compartment_id} value={String(compartment.compartment_id)}>
                    {compartment.name || `Compartiment ${compartment.compartment_id}`}
                  </MenuItem>
                ))}
              </Select>
              {compartments.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aucun compartiment disponible
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Informations surface (lecture seule) */}
          {sector.area_square_meters && typeof sector.area_square_meters === 'number' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Surface"
                value={`${sector.area_square_meters.toFixed(2)} m²`}
                disabled
                helperText="La surface est calculée automatiquement à partir de la délimitation"
              />
            </Grid>
          )}

          {/* Coordonnées GPS du point central */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Point GPS central (optionnel)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MyLocation />}
                onClick={handleGetLocation}
                disabled={loading || isGettingLocation}
              >
                {isGettingLocation ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Obtention...
                  </>
                ) : (
                  'Utiliser ma position'
                )}
              </Button>
              <Typography variant="caption" color="text.secondary">
                ou saisir manuellement
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              value={formData.gps_latitude}
              onChange={handleChange('gps_latitude')}
              placeholder="Ex: 35.088056"
              disabled={loading}
              type="number"
              inputProps={{ step: "0.000001" }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              value={formData.gps_longitude}
              onChange={handleChange('gps_longitude')}
              placeholder="Ex: -6.108250"
              disabled={loading}
              type="number"
              inputProps={{ step: "0.000001" }}
            />
          </Grid>

          {/* Aide */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Conseil:</strong> Pour modifier la délimitation du secteur, utilisez le mode édition de la carte.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {loading ? 'Modification...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSectorDialog;
